import { MoonIcon } from "@phosphor-icons/react/dist/csr/Moon";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import cn from "classnames";
import {
  type DragEndEvent,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { addDays, COLD_START_DAYS } from "@hicya/habits-shared/engine";
import { EMPTY_STATES } from "@hicya/habits-shared/data";

import { RouteLoading } from "@/components/layout/route-loading";
import { HabitIcon } from "@/components/ui/habit-icon";
import { HabitCard } from "@/components/habits/habit-card";
import { HabitRow } from "@/components/habits/habit-row";
import { StatsSidebar } from "@/components/layout/stats-sidebar";
import { Onboarding } from "@/components/onboarding/onboarding";
import { AddMorePrompt } from "@/components/retention/add-more-prompt";
import { ReturnOverlay } from "@/components/retention/return-overlay";
import { ScoreExplainer } from "@/components/retention/score-explainer";
import { BlockHeader } from "@/components/ui/block-header";
import { Fab } from "@/components/ui/fab";
import { MilestoneToast } from "@/components/ui/milestone-toast";
import { NoteSheet } from "@/components/ui/note-sheet";
import { UndoToast } from "@/components/ui/undo-toast";
import { useHabits } from "@/context/habits-context";
import { useUndo } from "@/context/undo-context";
import { useBreakpoint } from "@/hooks/use-breakpoint";
import { FILTER_UNCATEGORIZED, useDailyView } from "@/hooks/use-daily-view";
import { useMilestones } from "@/hooks/use-milestones";
import type { HabitDisplay, TimeBlock } from "@/hooks/use-daily-view";

import styles from "./today.module.scss";

import type { TimeOfDay } from "@hicya/habits-shared/db";

function generateId(): string {
  return crypto.randomUUID();
}

export function Today() {
  const { state } = useHabits();

  if (state.phase === "loading") {
    return (
      <div className={styles.container}>
        <RouteLoading />
      </div>
    );
  }

  // Onboarding: render before TodayView to avoid mounting DnD Kit, sensors,
  // and daily view hooks while onboarding is in progress. The onboarding has
  // 3 steps (template pick → privacy → first completion) and manages its own
  // step state, so we gate on onboardingCompleted, not habit count.
  if (!state.settings.onboardingCompleted) {
    return (
      <div className={styles.container}>
        <Onboarding />
      </div>
    );
  }

  return <TodayView />;
}

function TodayView() {
  const {
    state,
    completeHabit,
    incrementHabit,
    skipHabit,
    backfillHabit,
    reorderHabits,
    updateCompletionNote,
    updateSettings,
  } = useHabits();
  const { triggerUndo } = useUndo();
  const navigate = useNavigate();
  const [activeCategoryFilter, setActiveCategoryFilter] = useState<string | undefined>(undefined);
  const view = useDailyView(activeCategoryFilter);
  const { blocks, allHabits, totalDue, totalDone, layoutMode, today, currentBlock, isLightDay } =
    view;
  const { currentMilestone, dismissMilestone } = useMilestones();
  const breakpoint = useBreakpoint();
  const isDesktop = breakpoint === "desktop";

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const [collapsedBlocks, setCollapsedBlocks] = useState<Set<TimeOfDay>>(() => {
    // Past blocks start collapsed, current and future start expanded
    const blockOrder: TimeOfDay[] = ["morning", "afternoon", "evening"];
    const currentIdx = blockOrder.indexOf(currentBlock);
    const collapsed = new Set<TimeOfDay>();
    for (let i = 0; i < currentIdx; i++) {
      collapsed.add(blockOrder[i]);
    }
    return collapsed;
  });

  const [showReturnOverlay, setShowReturnOverlay] = useState(false);
  const [showScoreExplainer, setShowScoreExplainer] = useState(false);
  const [noteTarget, setNoteTarget] = useState<HabitDisplay | null>(null);

  // Track lastActiveDate and detect absence
  useEffect(() => {
    const { lastActiveDate, onboardingCompleted } = state.settings;
    if (!onboardingCompleted || allHabits.length === 0) return;

    if (lastActiveDate && lastActiveDate !== today) {
      // Calculate days since last active
      const last = new Date(lastActiveDate + "T12:00:00").getTime();
      const now = new Date(today + "T12:00:00").getTime();
      const daysSince = Math.round((now - last) / (24 * 60 * 60 * 1000));

      if (daysSince >= 3) {
        setShowReturnOverlay(true);
        return;
      }
    }

    // Update lastActiveDate to today
    if (lastActiveDate !== today) {
      void updateSettings("lastActiveDate", today);
    }
  }, [
    state.settings.lastActiveDate,
    state.settings.onboardingCompleted,
    allHabits.length,
    today,
    updateSettings,
  ]);

  const handleDismissReturn = useCallback(() => {
    setShowReturnOverlay(false);
    void updateSettings("lastActiveDate", today);
  }, [updateSettings, today]);

  // Detect cold-start end: show score explainer once any habit passes day 14
  useEffect(() => {
    if (state.settings.scoreRevealed) return;
    if (!state.settings.onboardingCompleted || allHabits.length === 0) return;

    const hasMatured = allHabits.some((d) => {
      const createdDate = d.habit.created_at.split("T")[0];
      const created = new Date(createdDate + "T12:00:00").getTime();
      const now = new Date(today + "T12:00:00").getTime();
      const daysSinceCreation = Math.round((now - created) / (24 * 60 * 60 * 1000));
      return daysSinceCreation >= COLD_START_DAYS;
    });

    if (hasMatured) {
      setShowScoreExplainer(true);
    }
  }, [state.settings.scoreRevealed, state.settings.onboardingCompleted, allHabits, today]);

  const handleDismissScoreExplainer = useCallback(() => {
    setShowScoreExplainer(false);
  }, []);

  // Compute average score for the score explainer (only non-cold-start habits)
  const averageScore = useMemo(() => {
    const scoredHabits = allHabits.filter((d) => !d.isColdStart);
    if (scoredHabits.length === 0) return 0;
    const total = scoredHabits.reduce((sum, d) => sum + d.score, 0);
    return total / scoredHabits.length;
  }, [allHabits]);

  const toggleBlock = useCallback((name: TimeOfDay) => {
    setCollapsedBlocks((prev) => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });
  }, []);

  const handleComplete = useCallback(
    async (display: HabitDisplay) => {
      const id = generateId();
      await completeHabit(display.habit.id, id);
      triggerUndo({
        habitId: display.habit.id,
        habitName: display.habit.name,
        previousValue: null,
      });
    },
    [completeHabit, triggerUndo],
  );

  const handleIncrement = useCallback(
    async (display: HabitDisplay) => {
      const id = generateId();
      await incrementHabit(display.habit.id, id);
      if ((display.completion?.value ?? 0) + 1 >= display.effectiveTarget) {
        triggerUndo({
          habitId: display.habit.id,
          habitName: display.habit.name,
          previousValue: display.completion?.value ?? 0,
        });
      }
    },
    [incrementHabit, triggerUndo],
  );

  const handleSkip = useCallback(
    async (display: HabitDisplay) => {
      const id = generateId();
      await skipHabit(display.habit.id, id);
    },
    [skipHabit],
  );

  const handleBackfill = useCallback(
    async (display: HabitDisplay) => {
      const id = generateId();
      const yesterday = addDays(today, -1);
      await backfillHabit(display.habit.id, yesterday, id);
    },
    [backfillHabit, today],
  );

  const handleAddNote = useCallback((display: HabitDisplay) => {
    setNoteTarget(display);
  }, []);

  const handleSaveNote = useCallback(
    async (note: string | null) => {
      if (noteTarget) {
        await updateCompletionNote(noteTarget.habit.id, note);
      }
    },
    [noteTarget, updateCompletionNote],
  );

  const handleEdit = useCallback(
    (display: HabitDisplay) => {
      void navigate(`/habits/${display.habit.id}/edit`);
    },
    [navigate],
  );

  const handleViewStats = useCallback(
    (display: HabitDisplay) => {
      void navigate(`/habits/${display.habit.id}/stats`);
    },
    [navigate],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      // Find which block both items belong to
      for (const block of blocks) {
        const ids = block.habits.map((h) => h.habit.id);
        const oldIndex = ids.indexOf(String(active.id));
        const newIndex = ids.indexOf(String(over.id));
        if (oldIndex !== -1 && newIndex !== -1) {
          const reordered = arrayMove(ids, oldIndex, newIndex);
          const updates = reordered.map((id, i) => ({ id, sort_order: i }));
          void reorderHabits(updates);
          break;
        }
      }
    },
    [blocks, reorderHabits],
  );

  // Format today's date
  const dateObj = new Date(today + "T12:00:00");
  const formattedDate = dateObj.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });

  // Empty state (onboarding completed but all habits deleted/archived)
  if (allHabits.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.empty}>
          <p className={styles.emptyText}>
            Start with one habit. What&apos;s something small you&apos;d like to do every day?
          </p>
          <button
            type="button"
            className={styles.emptyButton}
            onClick={() => navigate("/habits/new")}
          >
            Create your first habit
          </button>
        </div>
        <Fab />
      </div>
    );
  }

  // All done message
  const allDone = totalDue > 0 && totalDone >= totalDue;

  // "Add more" prompt: 1 habit, 3+ days of consistency, not dismissed
  const showAddMore =
    allHabits.length === 1 &&
    !state.settings.addMoreDismissed &&
    state.settings.onboardingCompleted &&
    state.settings.lastActiveDate !== "" &&
    (() => {
      const created = new Date(allHabits[0].habit.created_at).getTime();
      const now = new Date(today + "T12:00:00").getTime();
      return now - created >= 3 * 24 * 60 * 60 * 1000;
    })();

  return (
    <div className={styles.desktopLayout}>
      <div className={styles.primaryPanel}>
        <div className={styles.container}>
          {showReturnOverlay && (
            <ReturnOverlay habits={allHabits.map((d) => d.habit)} onDismiss={handleDismissReturn} />
          )}

          {!showReturnOverlay && showScoreExplainer && (
            <ScoreExplainer averageScore={averageScore} onDismiss={handleDismissScoreExplainer} />
          )}

          <div className={styles.header}>
            <div>
              <h2 className={styles.title}>Today</h2>
              <p className={styles.date}>{formattedDate}</p>
            </div>
            <div className={styles.progressInfo}>
              <button
                type="button"
                className={cn(styles.lightDayButton, isLightDay && styles.lightDayActive)}
                onClick={() =>
                  void updateSettings(`lightDay:${today}`, isLightDay ? "false" : "true")
                }
                aria-label={isLightDay ? "Disable light day" : "Enable light day"}
                aria-pressed={isLightDay}
                title={isLightDay ? "Light day active — targets halved" : "Toggle light day"}
              >
                <MoonIcon size={18} aria-hidden="true" />
              </button>
              <span className={styles.progressText}>
                {totalDone}/{totalDue}
              </span>
            </div>
          </div>

          <CategoryFilterBar
            activeFilter={activeCategoryFilter}
            onFilterChange={setActiveCategoryFilter}
          />

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            {layoutMode === "lean" ? (
              <LeanLayout
                blocks={blocks}
                onComplete={handleComplete}
                onIncrement={handleIncrement}
                onSkip={handleSkip}
                onBackfill={handleBackfill}
                onEdit={handleEdit}
                onViewStats={handleViewStats}
                onAddNote={handleAddNote}
              />
            ) : (
              <DenseLayout
                blocks={blocks}
                currentBlock={currentBlock}
                collapsedBlocks={collapsedBlocks}
                onToggleBlock={toggleBlock}
                onComplete={handleComplete}
                onIncrement={handleIncrement}
                onSkip={handleSkip}
                onBackfill={handleBackfill}
                onEdit={handleEdit}
                onViewStats={handleViewStats}
                onAddNote={handleAddNote}
              />
            )}
          </DndContext>

          {totalDone === 0 && totalDue > 0 && (
            <p className={styles.emptyHint}>{EMPTY_STATES.noCompletionsToday}</p>
          )}
          {allDone && <p className={styles.allDone}>All done for today</p>}
          {showAddMore && <AddMorePrompt />}
        </div>
      </div>

      {isDesktop && (
        <div className={styles.secondaryPanel}>
          <StatsSidebar
            allHabits={allHabits}
            totalDue={totalDue}
            totalDone={totalDone}
            today={today}
            completions={state.completions}
            firstDayOfWeek={state.settings.firstDayOfWeek}
          />
        </div>
      )}

      <Fab />
      <UndoToast />
      {currentMilestone && (
        <MilestoneToast milestoneId={currentMilestone} onDismiss={dismissMilestone} />
      )}
      {noteTarget && (
        <NoteSheet
          habitName={noteTarget.habit.name}
          initialNote={noteTarget.completion?.note ?? null}
          onSave={handleSaveNote}
          onClose={() => setNoteTarget(null)}
        />
      )}
    </div>
  );
}

// ─── Lean Layout ───

interface LeanLayoutProps {
  blocks: TimeBlock[];
  onComplete: (d: HabitDisplay) => void;
  onIncrement: (d: HabitDisplay) => void;
  onSkip: (d: HabitDisplay) => void;
  onBackfill: (d: HabitDisplay) => void;
  onEdit: (d: HabitDisplay) => void;
  onViewStats: (d: HabitDisplay) => void;
  onAddNote: (d: HabitDisplay) => void;
}

function LeanLayout({
  blocks,
  onComplete,
  onIncrement,
  onSkip,
  onBackfill,
  onEdit,
  onViewStats,
  onAddNote,
}: LeanLayoutProps) {
  const allHabits = blocks.flatMap((block) => block.habits);
  const ids = allHabits.map((d) => d.habit.id);

  return (
    <SortableContext items={ids} strategy={verticalListSortingStrategy}>
      <div className={styles.habitList}>
        {allHabits.map((display) => (
          <HabitCard
            key={display.habit.id}
            display={display}
            onComplete={() => void onComplete(display)}
            onIncrement={() => void onIncrement(display)}
            onSkip={() => void onSkip(display)}
            onBackfill={() => void onBackfill(display)}
            onEdit={() => onEdit(display)}
            onViewStats={() => onViewStats(display)}
            onAddNote={() => onAddNote(display)}
          />
        ))}
      </div>
    </SortableContext>
  );
}

// ─── Dense Layout ───

interface DenseLayoutProps {
  blocks: TimeBlock[];
  currentBlock: TimeOfDay;
  collapsedBlocks: Set<TimeOfDay>;
  onToggleBlock: (name: TimeOfDay) => void;
  onComplete: (d: HabitDisplay) => void;
  onIncrement: (d: HabitDisplay) => void;
  onSkip: (d: HabitDisplay) => void;
  onBackfill: (d: HabitDisplay) => void;
  onEdit: (d: HabitDisplay) => void;
  onViewStats: (d: HabitDisplay) => void;
  onAddNote: (d: HabitDisplay) => void;
}

function DenseLayout({
  blocks,
  currentBlock,
  collapsedBlocks,
  onToggleBlock,
  onComplete,
  onIncrement,
  onSkip,
  onBackfill,
  onEdit,
  onViewStats,
  onAddNote,
}: DenseLayoutProps) {
  return (
    <div className={styles.denseList}>
      {blocks.map((block) => {
        const isExpanded = !collapsedBlocks.has(block.name);
        const isCurrent = block.name === currentBlock;
        const ids = block.habits.map((d) => d.habit.id);

        return (
          <div key={block.name} className={styles.block}>
            <BlockHeader
              name={block.label}
              completed={block.completed}
              total={block.total}
              isExpanded={isExpanded}
              isCurrent={isCurrent}
              onToggle={() => onToggleBlock(block.name)}
              data-testid={`block-header-${block.name}`}
            />
            {isExpanded && (
              <SortableContext items={ids} strategy={verticalListSortingStrategy}>
                <div className={styles.blockContent}>
                  {block.habits.map((display) => (
                    <HabitRow
                      key={display.habit.id}
                      display={display}
                      onComplete={() => void onComplete(display)}
                      onIncrement={() => void onIncrement(display)}
                      onSkip={() => void onSkip(display)}
                      onBackfill={() => void onBackfill(display)}
                      onEdit={() => onEdit(display)}
                      onViewStats={() => onViewStats(display)}
                      onAddNote={() => onAddNote(display)}
                    />
                  ))}
                </div>
              </SortableContext>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Category Filter Bar ───

interface CategoryFilterBarProps {
  activeFilter: string | undefined;
  onFilterChange: (filter: string | undefined) => void;
}

function CategoryFilterBar({ activeFilter, onFilterChange }: CategoryFilterBarProps) {
  const { state } = useHabits();
  const { habits, categories } = state;

  // Build set of category IDs that have at least one habit
  const usedCategoryIds = useMemo(() => {
    const ids = new Set<string>();
    for (const h of habits) {
      if (h.category_id) ids.add(h.category_id);
    }
    return ids;
  }, [habits]);

  // Categories with assigned habits, sorted by sort_order
  const activeCategories = useMemo(
    () =>
      categories
        .filter((c) => usedCategoryIds.has(c.id))
        .sort((a, b) => a.sort_order - b.sort_order),
    [categories, usedCategoryIds],
  );

  // Any habits without a category?
  const hasUncategorized = useMemo(() => habits.some((h) => h.category_id === null), [habits]);

  // Only show when 2+ categories have habits
  if (activeCategories.length < 2) return null;

  return (
    <div className={styles.filterBar}>
      <button
        type="button"
        className={cn(styles.filterChip, activeFilter === undefined && styles.filterChipActive)}
        onClick={() => onFilterChange(undefined)}
      >
        All
      </button>
      {activeCategories.map((cat) => (
        <button
          key={cat.id}
          type="button"
          className={cn(styles.filterChip, activeFilter === cat.id && styles.filterChipActive)}
          onClick={() => onFilterChange(cat.id)}
        >
          <span className={styles.filterChipDot} style={{ backgroundColor: cat.color }} />
          <HabitIcon value={cat.icon} /> {cat.name}
        </button>
      ))}
      {hasUncategorized && (
        <button
          type="button"
          className={cn(
            styles.filterChip,
            activeFilter === FILTER_UNCATEGORIZED && styles.filterChipActive,
          )}
          onClick={() => onFilterChange(FILTER_UNCATEGORIZED)}
        >
          Uncategorized
        </button>
      )}
    </div>
  );
}
